import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useApi } from '#providers/api/index.ts';
import { useProjects } from '#providers/projects/index.ts';
import { getQueryLoadState } from '#providers/query/index.ts';
import { TermsContext } from './Terms.context.ts';
import {
  getTermsAcceptedQueryKey,
  getTermsTextQueryKey,
} from './Terms.helpers.ts';
import type { TTermsContextValue, TTermsProviderProps } from './Terms.types.ts';

export const TermsProvider = ({ children }: TTermsProviderProps) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { currentProjectId, requireProjectId } = useProjects();

  const acceptedKey = useMemo(
    () => getTermsAcceptedQueryKey(currentProjectId),
    [currentProjectId],
  );

  const acceptedQuery = useQuery({
    queryKey: acceptedKey,
    queryFn: currentProjectId
      ? async () => {
          const response = await api.terms.isTermsAccepted({
            project_id: currentProjectId,
          });

          return response.accepted_terms;
        }
      : skipToken,
  });

  const { data: isTermsAccepted, refetch: refetchAccepted } = acceptedQuery;

  const textQuery = useQuery({
    queryKey: getTermsTextQueryKey(currentProjectId),
    queryFn:
      currentProjectId && isTermsAccepted === false
        ? async () => {
            const response = await api.terms.getTermsTexts({
              project_id: currentProjectId,
            });

            return response;
          }
        : skipToken,
  });

  const { refetch: refetchText } = textQuery;

  const {
    mutateAsync: acceptTerms,
    isPending: isAcceptingTerms,
    error: acceptTermsError,
    data: acceptance,
  } = useMutation({
    mutationFn: () => api.terms.acceptTerms({ project_id: requireProjectId() }),
    onSuccess: async (response) => {
      await queryClient.cancelQueries({ queryKey: acceptedKey });
      queryClient.setQueryData(acceptedKey, response.accepted_terms);
    },
  });

  const { loadError: acceptedError } = getQueryLoadState(acceptedQuery);
  const { loadError: textError } = getQueryLoadState(textQuery);
  const isCheckPending = Boolean(currentProjectId) && acceptedQuery.isPending;
  const isTextPending = isTermsAccepted === false && textQuery.isPending;

  const reloadTerms = useCallback(async () => {
    if (acceptedError) {
      await refetchAccepted();

      return;
    }

    if (textError) {
      await refetchText();
    }
  }, [acceptedError, textError, refetchAccepted, refetchText]);

  const termsText = textQuery.data?.text;
  const termsUpdatedAt = textQuery.data?.updated_at;
  const isLoadingTerms = isCheckPending || isTextPending;
  const termsError = acceptedError ?? textError;
  const isAcceptanceRecorded = acceptance?.accepted_terms;

  const value = useMemo<TTermsContextValue>(
    () => ({
      termsText,
      termsUpdatedAt,
      isTermsAccepted,
      isLoadingTerms,
      termsError,
      isAcceptingTerms,
      isAcceptanceRecorded,
      acceptTermsError: acceptTermsError ?? undefined,
      acceptTerms,
      reloadTerms,
    }),
    [
      termsText,
      termsUpdatedAt,
      isTermsAccepted,
      isLoadingTerms,
      termsError,
      isAcceptingTerms,
      isAcceptanceRecorded,
      acceptTermsError,
      acceptTerms,
      reloadTerms,
    ],
  );

  return (
    <TermsContext.Provider value={value}>{children}</TermsContext.Provider>
  );
};
